// DS: DS4.1
/** A styled radio button control for forms */
import React, {
  ReactNode,
  InputHTMLAttributes,
  useImperativeHandle,
  useRef,
} from 'react';
import c from 'classnames';
import BiInput from '@wix/da-bi/pkg/Components/BiInput';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { setRef } from '../../utils/refHelpers';
import s from './RadioButton.scss';

export interface Props
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  name: string;
  value: string;
  className?: string;
  inputClassName?: string;
  /** @deprecated */
  withMargins?: boolean;
  size?: 'small' | 'medium';
  /** @deprecated use DS6.1 Selection List Item */
  children?: ReactNode;
  biData?: BiEvent;
}

export interface AvailableRefs {
  focus: () => void;
  label: () => HTMLLabelElement | null;
  input: () => HTMLInputElement | null;
}

const RadioButton = React.forwardRef<AvailableRefs, Props>(
  (
    {
      className,
      inputClassName,
      children,
      withMargins = false,
      size = 'medium',
      biData,
      ...rest
    },
    ref
  ) => {
    const labelRef = useRef<ReturnType<AvailableRefs['label']>>(null);
    const inputRef = useRef<ReturnType<AvailableRefs['input']>>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
      label: () => labelRef.current,
      input: () => inputRef.current,
    }));

    return (
      <label
        className={c(
          s['root'],
          withMargins && s['with-margins'],
          !!children && s['with-children'],
          s['new-colors'],
          s[size],
          className
        )}
        ref={labelRef}
      >
        <BiInput
          type="radio"
          data-checked={rest.checked}
          biData={biData}
          className={c(s['input'], inputClassName)}
          innerRef={node => setRef(inputRef, node)}
          {...rest}
        />
        {children}
      </label>
    );
  }
);
RadioButton.displayName = 'RadioButton';

export default RadioButton;
